import React, { useState, useEffect, useContext, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import './Restaurant.css'
import { useTranslation } from 'react-i18next'
import ExploreMenu from '../../components/ExploreMenu/ExploreMenu'
import FoodDisplay from '../../components/FoodDisplay/FoodDisplay'
import ImageComponent from '../../components/common/Image'
import RatingModal from '../../components/RatingModal/RatingModal'
import { StoreContext } from '../../Context/StoreContext'

const Restaurant = () => {
  const { t } = useTranslation()
  const { restaurantId } = useParams()
  const [restaurant, setRestaurant] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [category, setCategory] = useState('All')
  const [searchQuery, setSearchQuery] = useState('')
  const [showRatingModal, setShowRatingModal] = useState(false)
  const [showRestaurantInfo, setShowRestaurantInfo] = useState(false)
  const { url, setSelectedRestaurant, selectedRestaurant, restaurantList, fetchIndividualRestaurant } = useContext(StoreContext)

  // Main effect to load restaurant data
  useEffect(() => {
    const loadRestaurant = async () => {
      if (!restaurantId) return;
      
      console.log('Restaurant page: Loading restaurant data for ID:', restaurantId)
      
      // First, try to find in already loaded restaurants (for performance)
      if (restaurantList && restaurantList.length > 0) {
        const foundRestaurant = restaurantList.find(r => r._id === restaurantId)
        if (foundRestaurant) {
          console.log('Restaurant page: Found restaurant in context list:', foundRestaurant.name, foundRestaurant._id)
          setRestaurant(foundRestaurant)
          setSelectedRestaurant(foundRestaurant)
          setLoading(false)
          return;
        }
      }
      
      // If not found in context, fetch directly
      console.log('Restaurant page: Restaurant not in context, fetching directly')
      try {
        setLoading(true)
        console.log('Restaurant page: Fetching restaurant data for ID:', restaurantId)
        const response = await fetch(`${url}/api/restaurant/${restaurantId}`)
        const data = await response.json()
        
        if (data.success) {
          console.log('Restaurant page: Successfully fetched restaurant:', data.data.name)
          setRestaurant(data.data)
          
          // Update the global selected restaurant in StoreContext
          setSelectedRestaurant(data.data)
        } else {
          setError(data.message || t('restaurant.restaurant_not_found'))
        }
      } catch (error) {
        console.error('Error fetching restaurant:', error)
        setError(t('restaurant.failed_to_load'))
      } finally {
        setLoading(false)
      }
    };
    
    loadRestaurant();
  }, [restaurantId, restaurantList, url, setSelectedRestaurant, t])

  // Clear selected restaurant when component unmounts
  useEffect(() => {
    return () => {
      console.log('Restaurant page: Component unmounting')
      // Let the Home component handle clearing selectedRestaurant to prevent race conditions
    }
  }, [])

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null
    if (imagePath.startsWith('http')) return imagePath
    return `${url}${imagePath}`
  }

  const hasLogo = () => {
    return restaurant?.branding && restaurant.branding.logo
  }

  const hasBackground = () => {
    return restaurant?.branding && restaurant.branding.banner
  }

  const getCurrentDayHours = (businessHours) => {
    if (!businessHours || businessHours.length === 0) return t('restaurant.hours_not_available')
    
    const today = new Date().getDay()
    const todayHours = businessHours.find(hours => hours.day === today)
    
    if (!todayHours) return t('restaurant.closed_today')
    
    return `${todayHours.open} - ${todayHours.close}`
  }

  const getRestaurantStatus = (businessHours, operationalStatus) => {
    // Check for manual override first
    if (operationalStatus?.override && operationalStatus.override !== 'auto') {
      if (operationalStatus.override === 'open') {
        return <span className="status-open">{t('restaurant.open')}</span>
      } else if (operationalStatus.override === 'closed') {
        return <span className="status-closed">{t('restaurant.closed')}</span>
      }
    }

    // Fall back to automatic status based on business hours
    if (!businessHours || businessHours.length === 0) {
      return <span className="status-unknown">{t('restaurant.hours_not_available')}</span>
    }

    const now = new Date()
    const currentDay = now.getDay()
    const currentTime = now.getHours() * 60 + now.getMinutes() // Current time in minutes

    const todayHours = businessHours.find(hours => hours.day === currentDay)
    
    if (!todayHours) {
      return <span className="status-closed">{t('restaurant.closed')}</span>
    }

    // Parse open and close times
    const [openHour, openMin] = todayHours.open.split(':').map(Number)
    const [closeHour, closeMin] = todayHours.close.split(':').map(Number)
    
    const openTime = openHour * 60 + openMin
    const closeTime = closeHour * 60 + closeMin

    if (currentTime < openTime || currentTime > closeTime) {
      return <span className="status-closed">{t('restaurant.closed')}</span>
    } else if (closeTime - currentTime <= 60) { // Closing within an hour
      return <span className="status-closing-soon">{t('restaurant.closing_soon')}</span>
    } else {
      return <span className="status-open">{t('restaurant.open')}</span>
    }
  }

  const isRestaurantClosed = () => {
    // Check for manual override first
    if (restaurant?.operationalStatus?.override && restaurant.operationalStatus.override !== 'auto') {
      return restaurant.operationalStatus.override === 'closed'
    }

    // Fall back to automatic status based on business hours
    if (!restaurant?.businessHours || restaurant.businessHours.length === 0) {
      return false // If no hours set, assume open
    }

    const now = new Date()
    const currentDay = now.getDay()
    const currentTime = now.getHours() * 60 + now.getMinutes()

    const todayHours = restaurant.businessHours.find(hours => hours.day === currentDay)
    
    if (!todayHours) {
      return true // Closed today
    }

    const [openHour, openMin] = todayHours.open.split(':').map(Number)
    const [closeHour, closeMin] = todayHours.close.split(':').map(Number)
    
    const openTime = openHour * 60 + openMin
    const closeTime = closeHour * 60 + closeMin

    return currentTime < openTime || currentTime > closeTime
  }

  const handleSearch = (e) => {
    e.preventDefault()
    // The search will be handled by the context/food display components
    // We just need to update the search query state
  }

  const handleShare = async () => {
    const shareData = {
      title: `${restaurant.name} - DASHBASKET`,
      text: `Check out ${restaurant.name} on DASHBASKET! ${restaurant.description || ''}`,
      url: window.location.href
    }

    try {
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData)
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(window.location.href)
        alert(t('restaurant.link_copied'))
      }
    } catch (error) {
      console.error('Error sharing:', error)
      // Final fallback: manual copy
      const textArea = document.createElement('textarea')
      textArea.value = window.location.href
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      alert(t('restaurant.link_copied'))
    }
  }

  const handleRateRestaurant = () => {
    // Check if user is logged in
    const token = localStorage.getItem('token')
    if (!token) {
      alert(t('restaurant.login_to_rate'))
      return
    }
    
    // Open rating modal (we'll create this component next)
    setShowRatingModal(true)
  }

  const toggleRestaurantInfo = () => {
    setShowRestaurantInfo(!showRestaurantInfo)
  }

  if (loading) {
    return <div className="restaurant-loading">{t('restaurant.loading')}</div>
  }

  if (error) {
    return <div className="restaurant-error">{t('restaurant.error')}: {error}</div>
  }

  if (!restaurant) {
    return <div className="restaurant-error">{t('restaurant.restaurant_not_found')}</div>
  }

  return (
    <div className="restaurant-page">
      {/* Restaurant Header with Background */}
      <div 
        className="restaurant-header" 
        style={{
          backgroundImage: hasBackground() 
            ? `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(${getImageUrl(restaurant.branding.banner)})`
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}
      >
        {hasLogo() && (
          <div className="restaurant-logo-overlay">
            <ImageComponent 
              src={restaurant.branding.logo} 
              alt={`${restaurant.name} logo`} 
              width={100}
              height={100}
            />
          </div>
        )}
      </div>

      {/* Restaurant Info and Search Section */}
      <div className="restaurant-info-search-section">
        <div className="info-search-container">
          {/* Restaurant Header Row */}
          <div className="restaurant-header-row">
            <div className="restaurant-name-section">
              <h2 className="restaurant-name">{restaurant.name}</h2>
              <div className="restaurant-status-container">
                {getRestaurantStatus(restaurant.businessHours, restaurant.operationalStatus)}
              </div>
            </div>
            
            {/* Restaurant Actions */}
            <div className="restaurant-actions">
              {/* Info Toggle Button */}
              <button 
                onClick={toggleRestaurantInfo}
                className={`info-toggle-button ${showRestaurantInfo ? 'info-visible' : 'info-hidden'}`}
                title={showRestaurantInfo ? t('restaurant.hide_restaurant_info') : t('restaurant.show_restaurant_info')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {showRestaurantInfo ? (
                    <path d="M19 13H5v-2h14v2z" fill="currentColor"/>
                  ) : (
                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/>
                  )}
                </svg>
                <span className="button-text-desktop">{showRestaurantInfo ? t('restaurant.hide_details') : t('restaurant.show_details')}</span>
                <span className="button-text-mobile">{showRestaurantInfo ? t('restaurant.hide') : t('restaurant.show')}</span>
              </button>
              
              {/* Share Button */}
              <button 
                onClick={handleShare}
                className="share-button"
                title={t('restaurant.share_restaurant')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z" fill="currentColor"/>
                </svg>
                {t('restaurant.share')}
              </button>
              
              {/* Rating Button */}
              <button 
                onClick={handleRateRestaurant}
                className="rate-button"
                title={t('restaurant.rate_restaurant')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="currentColor"/>
                </svg>
                {t('restaurant.rate')}
              </button>
              
              {/* Restaurant Search Bar */}
              <div className="restaurant-search-area">
                <form onSubmit={handleSearch} className="restaurant-search-form">
                  <div className="search-input-wrapper">
                    <input
                      type="text"
                      placeholder={t('restaurant.search_menu', { name: restaurant.name })}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="restaurant-search-input"
                    />
                    <button type="submit" className="search-button">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Restaurant Description */}
          {showRestaurantInfo && restaurant.description && (
            <div className="restaurant-description-row">
              <p className="restaurant-description">{restaurant.description}</p>
            </div>
          )}
          
          {/* Restaurant Details Row */}
          {showRestaurantInfo && (
            <div className="restaurant-details-grid">
              {restaurant.cuisineType && restaurant.cuisineType.length > 0 && (
                <span className="detail-item">
                  <strong>{t('restaurant.cuisine')}:</strong> {restaurant.cuisineType.join(', ')}
                </span>
              )}
              
              {restaurant.priceRange && (
                <span className="detail-item">
                  <strong>{t('restaurant.price')}:</strong> {restaurant.priceRange}
                </span>
              )}
              
              <span className="detail-item">
                <strong>{t('restaurant.rating')}:</strong> ⭐ {Number(restaurant.rating || restaurant.averageRating || 0).toFixed(1)}
                {restaurant.reviewCount > 0 && ` (${restaurant.reviewCount} reviews)`}
              </span>
              
              {restaurant.businessHours && restaurant.businessHours.length > 0 && (
                <span className="detail-item">
                  <strong>{t('restaurant.hours')}:</strong> {getCurrentDayHours(restaurant.businessHours)}
                </span>
              )}
              
              {restaurant.contactInfo?.phone && (
                <span className="detail-item">
                  <strong>{t('restaurant.phone')}:</strong> {restaurant.contactInfo.phone}
                </span>
              )}
              
              {restaurant.address && (
                <span className="detail-item">
                  <strong>{t('restaurant.address')}:</strong> {[restaurant.address.street, restaurant.address.quartier, restaurant.address.city].filter(Boolean).join(', ')}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Restaurant Content */}
      <div className="restaurant-content">
        <ExploreMenu 
          setCategory={setCategory} 
          category={category} 
          hideTitle={true}
          restaurantId={restaurantId}
          searchQuery={searchQuery}
        />
        <FoodDisplay 
          category={category}
          restaurantId={restaurantId}
          searchQuery={searchQuery}
        />
      </div>

      {/* Rating Modal */}
      <RatingModal
        restaurant={restaurant}
        isOpen={showRatingModal}
        onClose={() => setShowRatingModal(false)}
      />
    </div>
  )
}

export default Restaurant 